import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FiEdit, FiTrash2, FiArrowLeft, FiCheckCircle } from 'react-icons/fi';
import dependencyService from '../../services/dependencyService';

const DependencyView = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [dependency, setDependency] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchDependency();
  }, [id]);

  const fetchDependency = async () => {
    try {
      const response = await dependencyService.getById(id);
      setDependency(response.data);
    } catch (error) {
      toast.error('Failed to fetch dependency');
      console.error('Error fetching dependency:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this dependency?')) {
      try {
        await dependencyService.delete(id);
        toast.success('Dependency deleted successfully');
        navigate('/app/dependencies');
      } catch (error) {
        toast.error('Failed to delete dependency');
        console.error('Error deleting dependency:', error);
      }
    }
  };

  const handleResolve = async () => {
    const notes = prompt('Enter resolution notes:');
    if (notes) {
      try {
        await dependencyService.resolve(id, notes);
        toast.success('Dependency resolved successfully');
        fetchDependency();
      } catch (error) {
        toast.error('Failed to resolve dependency');
        console.error('Error resolving dependency:', error);
      }
    }
  };

  const getStatusBadge = (status) => {
    const statusColors = {
      Active: 'badge-success',
      Resolved: 'badge-success',
      Blocked: 'badge-red',
      Cancelled: 'badge-danger'
    };
    return statusColors[status] || 'badge-default';
  };

  const getPriorityBadge = (priority) => {
    const priorityColors = {
      Low: 'badge-info',
      Medium: 'badge-warning',
      High: 'badge-danger',
      Critical: 'badge-danger-dark'
    };
    return priorityColors[priority] || 'badge-default';
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!dependency) {
    return (
      <div className="container mx-auto px-4 py-8">
        <div className="text-center">
          <p className="text-gray-500 text-lg">Dependency not found</p>
          <Link to="/dependencies" className="text-blue-600 hover:text-blue-800 mt-4 inline-block">
            Back to Dependencies
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <button
            onClick={() => navigate('/app/dependencies')}
            className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
          >
            <FiArrowLeft className="w-5 h-5 mr-2" />
            Back to Dependencies
          </button>
          <div className="flex justify-between items-start">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Dependency Details</h1>
              <p className="text-gray-600 mt-1">View dependency information</p>
            </div>
            <div className="flex space-x-2">
              {dependency.status === 'Active' && (
                <button
                  onClick={handleResolve}
                  className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
                >
                  <FiCheckCircle className="w-5 h-5" />
                  <span>Resolve</span>
                </button>
              )}
              <button
                onClick={() => navigate(`/app/dependencies/${id}/edit`)}
                className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
              >
                <FiEdit className="w-5 h-5" />
                <span>Edit</span>
              </button>
              <button
                onClick={handleDelete}
                className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg flex items-center space-x-2"
              >
                <FiTrash2 className="w-5 h-5" />
                <span>Delete</span>
              </button>
            </div>
          </div>
        </div>

        {/* Dependency Information */}
        <div className="bg-white rounded-lg shadow-md p-6 space-y-6">
          {/* Basic Info */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Entity Type</label>
              <p className="text-lg font-semibold text-gray-900">{dependency.entity_type}</p>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Dependency Type</label>
              <p className="text-lg font-semibold text-gray-900">{dependency.dependency_type}</p>
            </div>
          </div>

          {/* Status and Priority */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Status</label>
              <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${getStatusBadge(dependency.status)}`}>
                {dependency.status}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Priority</label>
              <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${getPriorityBadge(dependency.priority)}`}>
                {dependency.priority}
              </span>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-500 mb-1">Impact Level</label>
              <span className={`px-3 py-1 inline-flex text-sm font-semibold rounded-full ${getPriorityBadge(dependency.impact_level)}`}>
                {dependency.impact_level}
              </span>
            </div>
          </div>

          {/* From Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">From</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {dependency.project && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Project</label>
                  <p className="text-gray-900">{dependency.project.name}</p>
                  <p className="text-sm text-gray-500">{dependency.project.code}</p>
                </div>
              )}
              {dependency.task && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Task</label>
                  <p className="text-gray-900">{dependency.task.title}</p>
                </div>
              )}
            </div>
          </div>

          {/* Depends On Section */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Depends On</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {dependency.dependent_on_project && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Project</label>
                  <p className="text-gray-900">{dependency.dependent_on_project.name}</p>
                  <p className="text-sm text-gray-500">{dependency.dependent_on_project.code}</p>
                </div>
              )}
              {dependency.dependent_on_task && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Task</label>
                  <p className="text-gray-900">{dependency.dependent_on_task.title}</p>
                </div>
              )}
              {dependency.external_reference && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">External Reference</label>
                  <p className="text-gray-900">{dependency.external_reference}</p>
                </div>
              )}
            </div>
          </div>

          {/* Timing */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Timing</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Lag Days</label>
                <p className="text-gray-900">{dependency.lag_days || 0} days</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Lead Days</label>
                <p className="text-gray-900">{dependency.lead_days || 0} days</p>
              </div>
              {dependency.expected_resolution_date && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Expected Resolution</label>
                  <p className="text-gray-900">
                    {new Date(dependency.expected_resolution_date).toLocaleDateString()}
                  </p>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          {dependency.description && (
            <div className="border-t pt-6">
              <label className="block text-sm font-medium text-gray-500 mb-2">Description</label>
              <p className="text-gray-900 whitespace-pre-wrap">{dependency.description}</p>
            </div>
          )}

          {/* Resolution Notes */}
          {dependency.resolution_notes && (
            <div className="border-t pt-6">
              <label className="block text-sm font-medium text-gray-500 mb-2">Resolution Notes</label>
              <p className="text-gray-900 whitespace-pre-wrap">{dependency.resolution_notes}</p>
              {dependency.resolved_date && (
                <p className="text-sm text-gray-500 mt-2">
                  Resolved on: {new Date(dependency.resolved_date).toLocaleDateString()}
                </p>
              )}
            </div>
          )}

          {/* Metadata */}
          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Metadata</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Created By</label>
                <p className="text-gray-900">
                  {dependency.creator ? 
                    `${dependency.creator.first_name} ${dependency.creator.last_name}` : 
                    'N/A'}
                </p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Created At</label>
                <p className="text-gray-900">
                  {new Date(dependency.created_at).toLocaleString()}
                </p>
              </div>
              {dependency.updater && (
                <div>
                  <label className="block text-sm font-medium text-gray-500 mb-1">Last Updated By</label>
                  <p className="text-gray-900">
                    {`${dependency.updater.first_name} ${dependency.updater.last_name}`}
                  </p>
                </div>
              )}
              <div>
                <label className="block text-sm font-medium text-gray-500 mb-1">Last Updated</label>
                <p className="text-gray-900">
                  {new Date(dependency.updated_at).toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DependencyView;